import { createElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useHtmlToReact,
  type ElementTransform,
} from '@/components/Common/HtmlToReact';
import {
  BasisToggleButton,
  StatusTag,
} from '@/components/Approval/@components/OpenConstructionCost/parts';
import {
  canToggleSettlementBasis,
  mapSettlementResult,
  settlementStatusTagClass,
  type SettlementBasisMode,
} from '@/utils/approval/openConstructionCostSettlement';
import { OpenConstructionCostSettlementRoot } from './styles';

type Props = {
  /** 저장 시점 리포트 HTML(attributes.content) — 결재된 내용 그대로 */
  content: string;
  /** 판정기준 전환에만 쓰는 분석 결과 원문(attributes.data) */
  data: unknown[];
};

/**
 * 234 조회 — 레거시 appr_view_forms_open_construction_cost_settlement 와 같이 저장 HTML 을 그대로 그리고,
 * 기준 2벌이 모두 있을 때만 판정기준으로 바뀌는 칸을 data 로 다시 채움(레거시 applyBasis, 기본 직접이익)
 */
const OpenConstructionCostSettlementSnapshot = ({ content, data }: Props) => {
  const { t, i18n } = useTranslation();
  // 판정기준 칸 문구는 보는 사람 언어 — 언어를 바꾸면 다시 만듦
  const view = useMemo(() => mapSettlementResult(data), [data, i18n.language]);
  const [mode, setMode] = useState<SettlementBasisMode>('direct');
  const canToggle = !!view && canToggleSettlementBasis(view);
  const active = canToggle && view ? view.basis[mode] : null;

  const toggle = useCallback(
    () => setMode(mode === 'operating' ? 'direct' : 'operating'),
    [mode],
  );

  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props }) => {
      const key = props.key as string;
      if (element.id === 'btn_basis_toggle') {
        return (
          <BasisToggleButton
            key={key}
            basisMode={mode}
            hidden={!canToggle}
            onClick={canToggle ? toggle : undefined}
          />
        );
      }
      if (!active) return undefined;
      const text = (value: string) => createElement(tagName, props, value);
      const html = (value: string) =>
        createElement(tagName, {
          ...props,
          dangerouslySetInnerHTML: { __html: value },
        });
      const tag = (status: string, label?: string, hidden?: boolean) => (
        <StatusTag
          key={key}
          id={element.id}
          status={status}
          variant={settlementStatusTagClass(status)}
          label={label}
          hidden={hidden}
        />
      );
      const { kpiRecovery: kr, kpiProfit: kp, roi: r } = active;
      switch (element.id) {
        case 'value_overall_opinion':
          return active.opinionHTML ? html(active.opinionHTML) : undefined;
        case 'value_kpi_recovery_status':
          return tag(kr.status);
        case 'value_kpi_recovery_value':
          return text(kr.value || '-');
        case 'value_kpi_recovery_desc':
          return text(kr.desc || '-');
        case 'label_kpi_profit_title':
        case 'label_profit_subtitle':
          return text(active.profitTitle);
        case 'value_kpi_profit_status':
          return tag(kp.status);
        case 'value_kpi_profit_value':
          return text(kp.value || '-');
        case 'value_kpi_profit_desc':
          return text(kp.desc || '-');
        case 'value_roi_status':
          return tag(r.status, t('회수 속도'));
        case 'value_roi_rate_status':
          return tag(kp.status, t('이익률'), !kp.status);
        case 'value_recovery_standard':
          return text(r.standardRecovery || '-');
        case 'value_recovery_open':
          return text(r.openRecovery || '-');
        case 'value_recovery_settlement':
          return r.settlementRecovery
            ? createElement(
                tagName,
                props,
                <strong>{r.settlementRecovery}</strong>,
              )
            : text('-');
        case 'value_recovery_verdict':
          return text(r.recoveryVerdict);
        case 'label_profit_chip':
          return text(active.profitChip);
        case 'value_profit_standard':
          return html(r.standardProfitCell || '-');
        case 'value_profit_open':
          return html(r.openProfitCell || '-');
        case 'value_profit_settlement':
          return html(r.settlementProfitCell || '-');
        case 'value_profit_verdict':
          return text(r.profitMarginVerdict);
        default:
          return undefined;
      }
    },
    [active, canToggle, mode, t, toggle],
  );

  const rendered = useHtmlToReact(content, { transform });

  return (
    <OpenConstructionCostSettlementRoot className="open-construction-cost">
      {rendered}
    </OpenConstructionCostSettlementRoot>
  );
};

export default OpenConstructionCostSettlementSnapshot;
